import { X, CheckCircle, AlertTriangle, CheckCheck } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../ui/tooltip';
import { purchasingAPI, GoodsReceiptNote, DeliveryAppeal, POChangeLog } from '../../../lib/api';
import type { DeliveryAppealLog, DeliveryAppealAttachment } from '../../../lib/api/types/grn-types';
import { AcknowledgeGRNModal, DisputeGRNModal, ResolveGRNDisputeModal, type GRNWorkflowData } from '../GRNWorkflowModals';
import { useFormatters } from '../../../contexts/PreferencesContext';
import { useAuth } from '../../../contexts/AuthContext';
import { useMenuSettings } from '../../../contexts/MenuSettingsContext';
import { getApiErrorMessage } from '../../../utils/errorUtils';
import { toast } from 'sonner';
import RaiseAppealModal from './RaiseAppealModal';
import AppealResponseModal from './AppealResponseModal';
import GRNScoreChart from './GRNScoreChart';
import { computeAllocationSplit } from './allocationSplit';

interface DeliveryDetailsModalProps {
  grn: GoodsReceiptNote;
  /** When a single grn_number covers multiple PO lines, the parent passes
   *  every sister GRN record here. The modal then aggregates qty and
   *  merges schedule allocations across all lines in the Allocations tab. */
  grnGroup?: GoodsReceiptNote[];
  onClose: () => void;
  onRefresh?: () => void;
}

export default function DeliveryDetailsModal({ grn, grnGroup, onClose, onRefresh }: DeliveryDetailsModalProps) {
  const { user } = useAuth();
  const { isFeatureEnabled, isOpenOrdersControlHidden } = useMenuSettings();
  const isSupplier = user?.platform === 'SUPPLIER';
  // CR-5884: Open Orders supplier-side hide-toggles surface in this detail
  // modal too — the GRN delivery trend chart and the OTIF override (Appeal)
  // controls live here. Customer / Helix viewers always see them.
  const hideTrendLineForSupplier = isOpenOrdersControlHidden('hide_trend_line', isSupplier);
  const hideAppealForSupplier = isOpenOrdersControlHidden('hide_otif_override_button', isSupplier);
  const showAllocationsTab = isFeatureEnabled('po_line_schedules');
  const [showAcknowledgeModal, setShowAcknowledgeModal] = useState(false);
  const [showDisputeModal, setShowDisputeModal] = useState(false);
  const [showResolveModal, setShowResolveModal] = useState(false);
  const [showAppealModal, setShowAppealModal] = useState(false);
  const [showAppealResponseModal, setShowAppealResponseModal] = useState(false);
  const [grnData, setGrnData] = useState(grn);
  const [latestAppeal, setLatestAppeal] = useState<DeliveryAppeal | null>(null);
  const [appealLogs, setAppealLogs] = useState<DeliveryAppealLog[]>([]);
  const [appealAttachments, setAppealAttachments] = useState<DeliveryAppealAttachment[]>([]);
  // CR-4601 rework: GRN workflow audit trail (acknowledge / dispute / resolve events)
  const [workflowLogs, setWorkflowLogs] = useState<POChangeLog[]>([]);
  const [loading, setLoading] = useState(false);
  // CR-4869 round 3: Action clusters were gated on `!loading`, but `loading`
  // starts false — first paint happened before the fetch even began, so the
  // pop-up briefly rendered Acknowledge before swapping to Cancel Appeal once
  // latestAppeal arrived. Track initial-load completion separately so action
  // clusters stay suppressed until the appeal lookup has resolved at least
  // once.
  const [initialLoaded, setInitialLoaded] = useState(false);

  const companyType = user?.company_type;

  // Multi-line GRN aggregation. When grnGroup is passed, walk all sister
  // records and build a merged allocations list (each allocation tagged
  // with its source line_number + part_number so the modal can show
  // which line each schedule allocation came from). Totals used in the
  // Allocations tab.
  const sisters: GoodsReceiptNote[] = grnGroup && grnGroup.length > 1 ? grnGroup : [grnData];
  // Sort so allocations render with lower schedule_line_number at the
  // top (numerically — "2" before "3" before "11"), matching the
  // attribution walk order. Grouping by PO line first keeps
  // multi-line GRNs tidy (each line's schedules stay together).
  const groupAllocations = sisters
    .flatMap(s =>
      (s.allocations ?? []).map(a => ({
        ...a,
        line_number: s.line_number,
        part_number: s.part_number,
        po_number: s.po_number,
      }))
    )
    .sort((a, b) => {
      const lineCmp = (a.line_number ?? 0) - (b.line_number ?? 0);
      if (lineCmp !== 0) return lineCmp;
      const an = parseInt(a.schedule_line_number ?? '0', 10);
      const bn = parseInt(b.schedule_line_number ?? '0', 10);
      if (Number.isNaN(an) || Number.isNaN(bn)) {
        return (a.schedule_line_number ?? '').localeCompare(b.schedule_line_number ?? '');
      }
      return an - bn;
    });
  const groupTotalReceived = sisters.reduce((sum, s) => sum + Number(s.quantity_received || 0), 0);
  const isMultiLine = sisters.length > 1;
  // When schedule-aware (po_line_schedules), per-schedule detail
  // (schedule line #, per-schedule variance) lives in the Allocations
  // tab — the Details tab would duplicate it unhelpfully. Inferred from
  // the presence of allocations on any of the sister GRNs.
  const scheduleAware = sisters.some(s => (s.allocations?.length ?? 0) > 0);

  // Fetch full GRN details on mount to get workflow status
  useEffect(() => {
    if (grn.id) {
      fetchGRNDetails();
    }
  }, [grn.id]);

  const fetchGRNDetails = async () => {
    try {
      const response = await purchasingAPI.getGRN(grn.id);
      setGrnData(response.data);

      // CR-4601 rework: Fetch the workflow-status audit trail for this GRN so
      // the popup can show who acknowledged/disputed/resolved it and when.
      try {
        const logsResponse = await purchasingAPI.getChangelogs({
          grn: grn.id,
          field_changed: 'workflow_status',
          ordering: 'change_date',
        });
        setWorkflowLogs(logsResponse.data.results || []);
      } catch (err: unknown) {
        console.error('Failed to fetch GRN workflow audit trail:', err);
        setWorkflowLogs([]);
      }

      // If GRN has a latest appeal, fetch its details
      // CR-4678 round 5: Previously a cancelled appeal was wiped to null
      // here ("treat as no active appeal"), which also nuked the Appeal
      // History audit trail — Sarah's bounce: "the audit trail has
      // disappeared which needs to be re-initiated to show that an appeal
      // was submitted and then cancelled". Keep the appeal + logs visible
      // so the cancellation is still part of the visible record. The
      // action-button gating below uses status checks
      // (awaiting_response/queried for active, rejected/accepted for
      // resolved) and naturally treats 'cancelled' as not-active, so
      // Acknowledge + re-Appeal still surface for the re-appeal flow.
      if (response.data.latest_appeal?.id) {
        try {
          const appealResponse = await purchasingAPI.getDeliveryAppeal(response.data.latest_appeal.id);
          setLatestAppeal(appealResponse.data);
          try {
            const [logsResponse, attachmentsResponse] = await Promise.all([
              purchasingAPI.getDeliveryAppealLogs(response.data.latest_appeal.id),
              purchasingAPI.getAppealAttachments(response.data.latest_appeal.id),
            ]);
            setAppealLogs(logsResponse.data);
            setAppealAttachments(attachmentsResponse.data);
          } catch { /* ignore log/attachment fetch failure */ }
        } catch (err: unknown) {
          console.error('Failed to fetch appeal details:', err);
        }
      } else {
        // CR-4617: No appeal recorded at all — clear stale state.
        setLatestAppeal(null);
        setAppealLogs([]);
        setAppealAttachments([]);
      }
    } catch (error) {
      console.error('Failed to fetch GRN details:', error);
    } finally {
      // CR-4869 round 3: mark initial load complete (success or error) so the
      // action clusters can render. Using try/finally guarantees we don't
      // leave the user staring at an empty footer if the appeal lookup fails.
      setInitialLoaded(true);
    }
  };

  // Close on ESC key
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  const { formatDate: contextFormatDate } = useFormatters();

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    return contextFormatDate(dateString);
  };

  const getOntimeStatusBadge = (status: string) => {
    const statusMap: Record<string, { label: string; class: string }> = {
      on_time: { label: 'On Time', class: 'bg-green-100 text-green-800' },
      early: { label: 'Early', class: 'bg-blue-100 text-blue-800' },
      late: { label: 'Late', class: 'bg-red-100 text-red-800' },
    };
    const config = statusMap[status] || { label: status || '-', class: 'bg-gray-100 text-gray-800' };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.class}`}>
        {config.label}
      </span>
    );
  };

  // CR-4603: Directional status labels
  const getAppealStatusBadge = (status: string, isCustomerAppeal?: boolean) => {
    let label = status || '-';
    let cls = 'bg-gray-100 text-gray-800';
    if (status === 'awaiting_response') {
      label = isCustomerAppeal ? 'Awaiting Supplier Response' : 'Awaiting Customer Response';
      cls = 'bg-yellow-100 text-yellow-800';
    } else if (status === 'accepted') { label = 'Accepted'; cls = 'bg-green-100 text-green-800'; }
    else if (status === 'queried') { label = 'Query – Awaiting Supplier Response'; cls = 'bg-blue-100 text-blue-800'; }
    else if (status === 'rejected') { label = 'Rejected'; cls = 'bg-red-100 text-red-800'; }
    else if (status === 'cancelled') { label = 'Appeal Cancelled'; cls = 'bg-gray-200 text-gray-600'; }
    const config = { label, class: cls };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.class}`}>
        {config.label}
      </span>
    );
  };

  const handleWorkflowSubmit = async (data: GRNWorkflowData) => {
    setLoading(true);
    try {
      if (data.action === 'acknowledge') {
        await purchasingAPI.acknowledgeGRN(data.grnId, {
          comments: data.comments,
        });
      } else if (data.action === 'dispute') {
        await purchasingAPI.disputeGRN(data.grnId, {
          reason_id: data.reason_id as number,
          comments: data.comments,
        });
      } else if (data.action === 'resolve') {
        await purchasingAPI.resolveGRNDispute(data.grnId, {
          comments: data.comments,
        });
      }

      // Refresh GRN details
      await fetchGRNDetails();

      // Refresh parent list if callback provided
      if (onRefresh) {
        onRefresh();
      }
    } catch (error: unknown) {
      console.error('Workflow action failed:', error);
      toast.error(getApiErrorMessage(error, 'Failed to process workflow action'));
    } finally {
      setLoading(false);
    }
  };

  const handleAppealSuccess = async () => {
    // CR-4617: Clear stale appeal state before refreshing
    setLatestAppeal(null);
    setAppealLogs([]);
    setAppealAttachments([]);
    await fetchGRNDetails();
    if (onRefresh) {
      onRefresh();
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-40"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b shrink-0">
            <div className="flex items-center gap-3">
              <h2 className="text-xl font-bold">Delivery Details</h2>
              {/* CR-4704: Acknowledge status badge */}
              {grnData.workflow_status === 'acknowledged' && (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  <CheckCircle className="h-3 w-3" />
                  Acknowledged
                </span>
              )}
              {grnData.workflow_status === 'disputed' && (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                  <AlertTriangle className="h-3 w-3" />
                  Disputed
                </span>
              )}
              {grnData.workflow_status === 'pending_review' && (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                  Pending Review
                </span>
              )}
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Scrollable Content */}
          <div className="overflow-y-auto flex-1 min-h-0">
          <Tabs defaultValue="details" className="w-full">
            <TabsList className="mx-6 mt-4">
              <TabsTrigger value="details">Details</TabsTrigger>
              {showAllocationsTab && (
                <TabsTrigger value="allocations">
                  Schedule Allocation{groupAllocations.length > 0 ? ` (${groupAllocations.length})` : ''}
                </TabsTrigger>
              )}
            </TabsList>
            <TabsContent value="details" className="mt-0">
          {/* Content - 2 Column Layout */}
          <div className="p-6">
            <div className="grid grid-cols-2 gap-x-12 gap-y-6">
              {/* Left Column */}
              <div className="space-y-6">
                <div>
                  <label className="text-sm font-medium text-gray-500">GRN Number</label>
                  <p className="mt-1 text-base font-medium">{grnData.grn_number}</p>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-500">Customer</label>
                  <p className="mt-1 text-base">{grnData.customer_name || grnData.po_line_details?.po_details?.customer_details?.name || '-'}</p>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-500">Supplier Group</label>
                  <p className="mt-1 text-base">{grnData.supplier_group || '-'}</p>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-500">Supplier Site</label>
                  <p className="mt-1 text-base">{grnData.supplier_name || grnData.po_line_details?.po_details?.supplier_details?.name || '-'}</p>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-500">PO Number</label>
                  <p className="mt-1 text-base font-medium">{grnData.po_number || grnData.po_line_details?.po_details?.po_number || '-'}</p>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-500">Line Number</label>
                  <p className="mt-1 text-base">{grnData.line_number || grnData.po_line_details?.line_number || '-'}</p>
                </div>

                {!scheduleAware && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">Schedule Line</label>
                    <p className="mt-1 text-base">
                      {grnData.schedule_line_number != null && grnData.schedule_line_number !== ''
                        ? (grnData.schedule_line_number === '0' ? '#0 (historical)' : `#${grnData.schedule_line_number}`)
                        : '-'}
                    </p>
                  </div>
                )}

                <div>
                  <label className="text-sm font-medium text-gray-500">Part Number</label>
                  <p className="mt-1 text-base font-mono">{grnData.part_number || grnData.po_line_details?.part_number || '-'}</p>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-500">Part Description</label>
                  <p className="mt-1 text-base">{grnData.part_description || '-'}</p>
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-6">
                <div>
                  <label className="text-sm font-medium text-gray-500">Qty Ordered</label>
                  <p className="mt-1 text-base">{grnData.quantity_ordered != null ? Number(grnData.quantity_ordered).toFixed(3) : grnData.po_line_details?.quantity != null ? Number(grnData.po_line_details.quantity).toFixed(3) : '-'}</p>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-500">Qty Received</label>
                  <p className="mt-1 text-base">{grnData.quantity_received != null ? Number(grnData.quantity_received).toFixed(3) : '-'}</p>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-500">Delivery Date</label>
                  <p className="mt-1 text-base">{formatDate(grnData.receipt_date)}</p>
                </div>

                {/* CR-4607: MRP Date and ODD fields */}
                <div>
                  <label className="text-sm font-medium text-gray-500">MRP Date</label>
                  <p className="mt-1 text-base">{grnData.mrp_date ? formatDate(grnData.mrp_date) : '-'}</p>
                </div>

                {/* ODD + Variance are both anchored to the schedule_date of
                    the PO line schedule the GRN is attributed to (the same
                    reference `on_time_status` uses on the backend). Sourcing
                    ODD from the PO header (original_delivery_date /
                    po_delivery_date) produced a stale pre-forecast date and
                    a misleading variance — e.g. GRN received on schedule
                    date 2026-04-20 shown as +447 days late vs the 2025
                    header commitment. For line-0 attributions schedule_date
                    is NULL (historical catch-all), so ODD + Variance render
                    blank. */}
                {(() => {
                  const odd = grnData.schedule_date;
                  const delivered = grnData.receipt_date;
                  return (
                    <>
                      <div>
                        <label className="text-sm font-medium text-gray-500">ODD (Original Due Date)</label>
                        <p className="mt-1 text-base">{odd ? formatDate(odd) : '-'}</p>
                      </div>

                      {/* On-Time Status at GRN header level is undefined
                          on schedule-aware tenants — per-allocation
                          verdicts live in the Schedule Allocation tab
                          (each row there shows its own OTIF / Variance). */}
                      {!scheduleAware && (
                        <div>
                          <label className="text-sm font-medium text-gray-500">On-Time Status</label>
                          <div className="mt-1">
                            {grnData.on_time_status ? getOntimeStatusBadge(grnData.on_time_status) : '-'}
                          </div>
                        </div>
                      )}

                      {!scheduleAware && (
                        <div>
                          <label className="text-sm font-medium text-gray-500">Variance (Days)</label>
                          <div className="mt-1">
                            {(() => {
                              if (!odd || !delivered) return <span className="text-base">-</span>;
                              const diffMs = new Date(delivered).getTime() - new Date(odd).getTime();
                              const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
                              const badge = diffDays > 0
                                ? 'bg-red-100 text-red-800'
                                : diffDays < 0
                                  ? 'bg-blue-100 text-blue-800'
                                  : 'bg-green-100 text-green-800';
                              const label = diffDays > 0
                                ? `+${diffDays} days (late)`
                                : diffDays < 0
                                  ? `${diffDays} days (early)`
                                  : '0 days (on time)';
                              return (
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${badge}`}>
                                  {label}
                                </span>
                              );
                            })()}
                          </div>
                        </div>
                      )}
                    </>
                  );
                })()}

              </div>
            </div>


            {/* CR-4601 rework: GRN Workflow audit trail (Acknowledge / Dispute / Resolve) */}
            {(workflowLogs.length > 0 || grnData.workflow_status === 'acknowledged') && (
              <div className="mt-6 border-t pt-6">
                <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  Acknowledgment
                </h3>
                {/* Latest state summary card */}
                <div className="bg-green-50 rounded-lg p-4 space-y-2 text-sm mb-3">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500">Current Status:</span>
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 capitalize">
                      {(grnData.workflow_status || 'pending_review').replace('_', ' ')}
                    </span>
                  </div>
                  {grnData.acknowledged_by_details && (
                    <div>
                      <span className="text-gray-500">Acknowledged By:</span>{' '}
                      <span className="font-medium">
                        {grnData.acknowledged_by_details.full_name || grnData.acknowledged_by_details.email}
                      </span>
                    </div>
                  )}
                  {grnData.acknowledged_date && (
                    <div>
                      <span className="text-gray-500">Acknowledged On:</span>{' '}
                      <span className="font-medium">{formatDate(grnData.acknowledged_date)}</span>
                    </div>
                  )}
                  {grnData.acknowledgment_comments && (
                    <div>
                      <span className="text-gray-500">Comments:</span>{' '}
                      <span>{grnData.acknowledgment_comments}</span>
                    </div>
                  )}
                </div>

                {/* CR-4661: History table — matches Appeal History column structure
                    (When, Company NAME, User, Description, Attachments) */}
                {workflowLogs.length > 0 && (
                  <div>
                    <h4 className="text-xs font-semibold text-gray-500 mb-2">Acknowledgment History</h4>
                    <div className="border rounded-lg overflow-hidden">
                      <table className="w-full text-xs">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="text-left py-2 px-3 font-medium">When</th>
                            <th className="text-left py-2 px-3 font-medium">Company</th>
                            <th className="text-left py-2 px-3 font-medium">User</th>
                            <th className="text-left py-2 px-3 font-medium">Description</th>
                            <th className="text-left py-2 px-3 font-medium">Attachments</th>
                          </tr>
                        </thead>
                        <tbody>
                          {workflowLogs.map((log) => {
                            const describeAction = (newVal: string, oldVal: string): string => {
                              if (newVal === 'acknowledged') return 'Delivery acknowledged';
                              if (newVal === 'disputed') return 'Dispute raised';
                              if (newVal === 'resolved') return 'Dispute resolved';
                              return `${(oldVal || 'pending review').replace(/_/g, ' ')} → ${(newVal || '').replace(/_/g, ' ')}`;
                            };
                            // Strip the "<status> by <type>." prefix the backend adds so the
                            // description column shows the user note cleanly (like Appeal History).
                            const rawReason = log.reason || '';
                            const cleanedNote = rawReason
                              .replace(/^(GRN\s+)?(acknowledged|disputed|resolved)(\s+by\s+\w+)?[.]?\s*/i, '')
                              .trim();
                            return (
                              <tr key={log.id} className="border-t">
                                <td className="py-2 px-3 text-gray-600">{formatDate(log.change_date)}</td>
                                <td className="py-2 px-3">
                                  {log.changed_by_company
                                    || log.changed_by_details?.companyName
                                    || (log.company_type ? log.company_type.charAt(0).toUpperCase() + log.company_type.slice(1) : '-')}
                                </td>
                                <td className="py-2 px-3">
                                  {log.changed_by_details?.full_name || log.changed_by_details?.email || 'System'}
                                </td>
                                <td className="py-2 px-3">
                                  <span className="font-medium">{describeAction(log.new_value, log.old_value || '')}</span>
                                  {cleanedNote && <span className="text-gray-500"> — {cleanedNote}</span>}
                                </td>
                                <td className="py-2 px-3 text-gray-400">-</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* CR-4611: Restructured Latest Appeal Section */}
            {grnData.latest_appeal && latestAppeal && (
              <div className="mt-6 border-t pt-6">
                <h3 className="text-sm font-semibold mb-3">Latest Appeal (#{latestAppeal.id})</h3>
                <div className="bg-orange-50 rounded-lg p-4 space-y-2 text-sm">
                  {/* Status row with badge aligned right */}
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500">Status:</span>
                    {getAppealStatusBadge(latestAppeal.status, latestAppeal.is_customer_appeal)}
                  </div>
                  {/* Qty Appealed: From X to Y — CR-4677: use snapshot */}
                  {latestAppeal.qty_claimed && (
                    <div>
                      <span className="text-gray-500">Qty Appealed:</span>{' '}
                      <span>From <span className="font-medium">{latestAppeal.original_qty || grnData.quantity_received}</span> to <span className="font-medium text-orange-700">{latestAppeal.qty_claimed}</span></span>
                    </div>
                  )}
                  {/* Delivery Date Appealed: From X to Y */}
                  {/* CR-4677: Use original_receipt_date (snapshot at appeal creation) so
                      the "from" value isn't overwritten after acceptance. Falls back to
                      grnData.receipt_date for appeals created before the snapshot field. */}
                  {latestAppeal.date_claimed && (
                    <div>
                      <span className="text-gray-500">Delivery Date Appealed:</span>{' '}
                      <span>From <span className="font-medium">{formatDate(latestAppeal.original_receipt_date || grnData.receipt_date)}</span> to <span className="font-medium text-orange-700">{formatDate(latestAppeal.date_claimed)}</span></span>
                    </div>
                  )}
                  {/* ODD Appealed: From X to Y */}
                  {latestAppeal.odd_claimed && (
                    <div>
                      <span className="text-gray-500">ODD Appealed:</span>{' '}
                      <span>From <span className="font-medium">{grnData.po_delivery_date ? formatDate(grnData.po_delivery_date) : '-'}</span> to <span className="font-medium text-orange-700">{formatDate(latestAppeal.odd_claimed)}</span></span>
                    </div>
                  )}
                  {latestAppeal.customer_note && (
                    <div><span className="text-gray-500">Customer Note:</span> <span>{latestAppeal.customer_note}</span></div>
                  )}
                </div>

                {/* CR-4611: Appeal History table with company, user, description, attachments */}
                {appealLogs.length > 0 && (
                  <div className="mt-4">
                    <h4 className="text-xs font-semibold text-gray-500 mb-2">Appeal History</h4>
                    <div className="border rounded-lg overflow-hidden">
                      <table className="w-full text-xs">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="text-left py-2 px-3 font-medium">When</th>
                            <th className="text-left py-2 px-3 font-medium">Company</th>
                            <th className="text-left py-2 px-3 font-medium">User</th>
                            <th className="text-left py-2 px-3 font-medium">Description</th>
                            <th className="text-left py-2 px-3 font-medium">Attachments</th>
                          </tr>
                        </thead>
                        <tbody>
                          {appealLogs.map((log) => {
                            // Show attachments only on the 'created' log entry
                            const showAttachments = log.action === 'created' && appealAttachments.length > 0;
                            return (
                              <tr key={log.id} className="border-t">
                                <td className="py-2 px-3 text-gray-600">{formatDate(log.performed_at)}</td>
                                <td className="py-2 px-3">{log.performed_by_company || '-'}</td>
                                <td className="py-2 px-3">{log.performed_by_name || 'System'}</td>
                                <td className="py-2 px-3">
                                  <span className="capitalize font-medium">{log.action}</span>
                                  {log.notes && <span className="text-gray-500"> — {log.notes}</span>}
                                </td>
                                <td className="py-2 px-3">
                                  {showAttachments ? (
                                    <div className="space-y-1">
                                      {appealAttachments.map((att) => (
                                        <button
                                          key={att.id}
                                          onClick={async () => {
                                            try {
                                              const res = await purchasingAPI.downloadAppealAttachment(latestAppeal!.id, att.id);
                                              const url = window.URL.createObjectURL(new Blob([res.data]));
                                              const link = document.createElement('a');
                                              link.href = url;
                                              link.download = att.file_name;
                                              link.click();
                                              window.URL.revokeObjectURL(url);
                                            } catch {
                                              toast.error('Failed to download attachment');
                                            }
                                          }}
                                          className="block text-blue-600 hover:underline text-left"
                                          title={att.file_name}
                                        >
                                          {att.file_name.length > 25 ? att.file_name.slice(0, 22) + '...' : att.file_name}
                                        </button>
                                      ))}
                                    </div>
                                  ) : (
                                    <span className="text-gray-400">-</span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* GRN Score Chart.
              CR-5884: Suppliers can have this chart hidden via Admin Setup →
              Purchasing → Open Orders → Hide Trend Line. */}
          {grnData.po_line && !hideTrendLineForSupplier && (
            <div className="px-6">
              <GRNScoreChart poLineId={grnData.po_line} poNumber={grnData.po_number} lineNumber={grnData.line_number} />
            </div>
          )}

            </TabsContent>
            {showAllocationsTab && (
            <TabsContent value="allocations" className="mt-0">
              <div className="p-6">
                {groupAllocations.length > 0 ? (
                  <>
                    <div className="mb-4 flex items-center gap-6 text-sm">
                      <div>
                        <span className="text-gray-500">Total delivered on this GRN:</span>{' '}
                        <span className="font-semibold">{Number(groupTotalReceived).toFixed(3)}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Sum of allocations:</span>{' '}
                        <span className="font-semibold">
                          {groupAllocations.reduce((sum, a) => sum + Number(a.allocated_qty || 0), 0).toFixed(3)}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-500">Allocations:</span>{' '}
                        <span className="font-semibold">{groupAllocations.length}</span>
                      </div>
                      {isMultiLine && (
                        <div>
                          <span className="text-gray-500">Lines covered:</span>{' '}
                          <span className="font-semibold">{sisters.length}</span>
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mb-3">
                      For each schedule line: <b>Original Scheduled</b> = <b>Already
                      Satisfied</b> (qty received from earlier deliveries) +
                      <b> This GRN</b> (qty attributed from this delivery) +
                      <b> Balance Due</b> (still unfulfilled). The attribution
                      walks schedule lines earliest-first.
                    </p>
                    <div className="flex items-center gap-4 text-xs text-gray-600 mb-3">
                      <div className="flex items-center gap-1.5">
                        <span className="inline-block w-3 h-3 bg-gray-400 rounded-sm" />
                        Already Satisfied
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className="inline-block w-3 h-3 bg-blue-500 rounded-sm" />
                        This GRN
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className="inline-block w-3 h-3 bg-amber-200 rounded-sm border border-amber-300" />
                        Balance Due
                      </div>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-sm">
                        <thead>
                          <tr className="text-left text-gray-500 border-b">
                            {isMultiLine && <th className="pb-2 pr-4 whitespace-nowrap">Line / Part</th>}
                            <th className="pb-2 pr-4 whitespace-nowrap">Sched Line</th>
                            <th className="pb-2 pr-4 whitespace-nowrap">Sched Date</th>
                            <th className="pb-2 pr-4 whitespace-nowrap">Delivered</th>
                            <th className="pb-2 pr-4 text-right whitespace-nowrap">Original Scheduled</th>
                            <th className="pb-2 pr-4 text-right whitespace-nowrap" title="Qty already satisfied on this schedule by earlier GRNs">Already Satisfied</th>
                            <th className="pb-2 pr-4 text-right whitespace-nowrap" title="Qty this GRN contributed to this schedule line">This GRN</th>
                            <th className="pb-2 pr-4 text-right whitespace-nowrap" title="Qty still unfulfilled on this schedule line (schedule_qty - shipped_qty)">Balance Due</th>
                            <th className="pb-2 pr-4 whitespace-nowrap" title="Visual breakdown of the schedule line: grey = prior GRNs, blue = this GRN, amber = balance due">Allocation</th>
                            <th className="pb-2 pr-4 whitespace-nowrap">OTIF / Variance</th>
                            <th className="pb-2 whitespace-nowrap">Notes</th>
                          </tr>
                        </thead>
                        <tbody>
                          {groupAllocations.map((a) => {
                            const sister = sisters.find(s => s.line_number === a.line_number);
                            const delivered = sister?.receipt_date || grnData.receipt_date;
                            let otifEl: React.ReactNode = '-';
                            if (a.schedule_date && delivered) {
                              const diffMs = new Date(delivered).getTime() - new Date(a.schedule_date).getTime();
                              const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
                              const badge = diffDays > 0 ? 'bg-red-100 text-red-800' : diffDays < 0 ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800';
                              const label = diffDays > 0 ? `+${diffDays}d late` : diffDays < 0 ? `${Math.abs(diffDays)}d early` : 'on time';
                              otifEl = <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${badge}`}>{label}</span>;
                            } else if (a.on_time_status) {
                              const badge = a.on_time_status === 'on_time' ? 'bg-green-100 text-green-800' : a.on_time_status === 'early' ? 'bg-blue-100 text-blue-800' : 'bg-red-100 text-red-800';
                              otifEl = <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${badge}`}>{a.on_time_status}</span>;
                            }
                            return (
                            <tr key={a.id} className="border-b last:border-b-0">
                              {isMultiLine && (
                                <td className="py-2 pr-4 text-xs whitespace-nowrap">
                                  <span className="font-medium">Line {a.line_number}</span>
                                  <span className="text-gray-400"> · </span>
                                  <span className="font-mono">{a.part_number}</span>
                                </td>
                              )}
                              <td className="py-2 pr-4 whitespace-nowrap">
                                {a.schedule_line_number === '0' ? '#0' : `#${a.schedule_line_number}`}
                              </td>
                              <td className="py-2 pr-4 whitespace-nowrap">{a.schedule_date ? formatDate(a.schedule_date) : '-'}</td>
                              <td className="py-2 pr-4 whitespace-nowrap">{delivered ? formatDate(delivered) : '-'}</td>
                              <td className="py-2 pr-4 text-right whitespace-nowrap">{a.schedule_qty ? Number(a.schedule_qty).toFixed(3) : '-'}</td>
                              <td className="py-2 pr-4 text-right whitespace-nowrap text-gray-600">
                                {(() => {
                                  if (a.schedule_qty == null) return '-';
                                  const { prior } = computeAllocationSplit(a);
                                  const siblings = a.sibling_allocations ?? [];
                                  if (siblings.length === 0) return prior.toFixed(3);
                                  return (
                                    <TooltipProvider delayDuration={150}>
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <span className="underline decoration-dotted cursor-help">
                                            {prior.toFixed(3)}
                                          </span>
                                        </TooltipTrigger>
                                        <TooltipContent side="top" className="max-w-sm">
                                          <div className="text-xs">
                                            <div className="font-semibold mb-1">Satisfied by earlier GRNs:</div>
                                            <table className="text-xs">
                                              <tbody>
                                                {siblings.map((s, i) => (
                                                  <tr key={i}>
                                                    <td className="pr-3 font-mono">{s.grn_number}</td>
                                                    <td className="pr-3 text-right">{Number(s.allocated_qty).toFixed(3)}</td>
                                                    <td className="text-gray-400">{s.receipt_date || ''}</td>
                                                  </tr>
                                                ))}
                                              </tbody>
                                            </table>
                                          </div>
                                        </TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                  );
                                })()}
                              </td>
                              <td className="py-2 pr-4 text-right font-medium whitespace-nowrap">{Number(a.allocated_qty).toFixed(3)}</td>
                              <td className="py-2 pr-4 text-right whitespace-nowrap">
                                {a.schedule_qty != null ? computeAllocationSplit(a).balance.toFixed(3) : '-'}
                              </td>
                              <td className="py-2 pr-4 whitespace-nowrap">
                                {(() => {
                                  const { sched, prior, thisGrn, balance } = computeAllocationSplit(a);
                                  if (sched <= 0) return '-';
                                  const priorPct = (prior / sched) * 100;
                                  const thisPct = (thisGrn / sched) * 100;
                                  const balPct = Math.max(0, 100 - priorPct - thisPct);
                                  return (
                                    <div
                                      className="w-32 h-4 bg-gray-50 rounded border border-gray-200 overflow-hidden flex"
                                      title={`Already Satisfied: ${prior.toFixed(3)} · This GRN: ${thisGrn.toFixed(3)} · Balance Due: ${balance.toFixed(3)} / ${sched.toFixed(3)}`}
                                    >
                                      {priorPct > 0 && <div className="bg-gray-400 h-full" style={{ width: `${priorPct}%` }} />}
                                      {thisPct > 0 && <div className="bg-blue-500 h-full" style={{ width: `${thisPct}%` }} />}
                                      {balPct > 0 && <div className="bg-amber-200 h-full" style={{ width: `${balPct}%` }} />}
                                    </div>
                                  );
                                })()}
                              </td>
                              <td className="py-2 pr-4 whitespace-nowrap">{otifEl}</td>
                              <td className="py-2 whitespace-nowrap">
                                {a.is_over_delivery && (
                                  <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                                    over-delivery
                                  </span>
                                )}
                              </td>
                            </tr>
                            );
                          })}
                        </tbody>
                        <tfoot>
                          <tr className="border-t-2 font-semibold">
                            <td className="py-2 pr-4" colSpan={isMultiLine ? 7 : 6}>Total (this GRN)</td>
                            <td className="py-2 pr-4 text-right">
                              {groupAllocations.reduce((sum, a) => sum + Number(a.allocated_qty || 0), 0).toFixed(3)}
                            </td>
                            <td colSpan={4}></td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </>
                ) : (
                  <p className="text-sm text-gray-500">No schedule allocations recorded for this GRN.</p>
                )}
              </div>
            </TabsContent>
            )}
          </Tabs>
          </div>{/* End scrollable content */}

          {/* Footer */}
          <div className="flex justify-between items-center p-6 border-t shrink-0">
            <div className="flex gap-3">
              {/* Supplier View */}
              {/* CR-4600: Supplier view — only Acknowledge, no Raise Dispute.
                  CR-4869 round 2: gate the entire supplier action cluster on
                  !loading. The pop-up renders before the appeal-lookup API
                  call resolves; while latestAppeal is still null, the
                  Acknowledge branch below its gate evaluated true and the
                  button flashed green for a frame before flipping to Cancel
                  Appeal once latestAppeal loaded. Suppressing the cluster
                  during the loading window avoids that visual pop. */}
              {companyType === 'supplier' && !loading && initialLoaded && (
                <>
                  {/* CR-4869: Hide Acknowledge when there's any active
                      (awaiting_response / queried) appeal — the supplier
                      shouldn't be able to acknowledge a delivery while an
                      appeal of either side is still in flight. Acknowledge
                      returns once the appeal is resolved / cancelled. */}
                  {grnData.workflow_status === 'pending_review'
                    && !(latestAppeal
                      && ['awaiting_response', 'queried'].includes(latestAppeal.status)) && (
                    <button
                      onClick={() => setShowAcknowledgeModal(true)}
                      disabled={loading}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                    >
                      <CheckCircle className="h-4 w-4" />
                      Acknowledge
                    </button>
                  )}
                  {grnData.workflow_status === 'disputed' && (
                    <button
                      onClick={() => setShowResolveModal(true)}
                      disabled={loading}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                    >
                      <CheckCheck className="h-4 w-4" />
                      Resolve Dispute
                    </button>
                  )}
                  {/* CR-4601 rework: Appeal closed once GRN is acknowledged.
                      Also suppress when a customer-raised appeal is still
                      active — the supplier should respond to that, not
                      stack another appeal on top.
                      CR-5884: Suppliers can have these Appeal /
                      Update-Appeal / Cancel-Appeal controls hidden via
                      Admin Setup → Purchasing → Open Orders → Hide OTIF
                      Override Button. */}
                  {!hideAppealForSupplier && grnData.can_appeal && grnData.workflow_status !== 'acknowledged'
                    && !(latestAppeal
                      && latestAppeal.is_customer_appeal
                      && ['awaiting_response', 'queried'].includes(latestAppeal.status)) && (
                    <button
                      onClick={() => setShowAppealModal(true)}
                      disabled={loading}
                      className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                    >
                      <AlertTriangle className="h-4 w-4" />
                      Appeal
                    </button>
                  )}
                  {!hideAppealForSupplier && latestAppeal?.status === 'queried' && (
                    <button
                      onClick={() => setShowAppealModal(true)}
                      disabled={loading}
                      className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                    >
                      <AlertTriangle className="h-4 w-4" />
                      Update Appeal
                    </button>
                  )}
                  {!hideAppealForSupplier && latestAppeal && !latestAppeal.is_customer_appeal && ['awaiting_response', 'queried'].includes(latestAppeal.status) && (
                    <button
                      onClick={async () => {
                        if (!window.confirm('Are you sure you want to cancel this appeal?')) return;
                        try {
                          setLoading(true);
                          await purchasingAPI.cancelDeliveryAppeal(latestAppeal.id);
                          toast.success('Appeal cancelled');
                          await handleAppealSuccess();
                        } catch (err: unknown) {
                          toast.error(getApiErrorMessage(err, 'Failed to cancel appeal'));
                        } finally {
                          setLoading(false);
                        }
                      }}
                      disabled={loading}
                      className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors disabled:opacity-50 flex items-center gap-2"
                    >
                      <X className="h-4 w-4" />
                      Cancel Appeal
                    </button>
                  )}
                  {/* Customer-raised appeal awaiting supplier response →
                      supplier responds. Customer appeals used to auto-accept,
                      so the supplier never had a UI to act on them. */}
                  {latestAppeal && latestAppeal.is_customer_appeal && latestAppeal.status === 'awaiting_response' && (
                    <button
                      onClick={() => setShowAppealResponseModal(true)}
                      disabled={loading}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                    >
                      <CheckCircle className="h-4 w-4" />
                      Respond to Appeal
                    </button>
                  )}
                </>
              )}

              {/* Customer / Helix Admin View — CR-4649: Action logic based on
                  delivery status and appeal state:
                  - On Time + no appeal → no actions (truly nothing to do)
                  - Early/Late + no appeal → Acknowledge + Appeal
                  - Pending appeal → Respond to Appeal (Accept/Query/Reject via modal)
                  - Disputed → Resolve Dispute
                  CR-4649 round 2: previously we offered a bare Acknowledge
                  on On-Time pending_review rows; Jacqueline rejected that
                  because no action is required in that state. */}
              {/* CR-4869 round 2: same !loading gate on the customer side
                  so the Respond-to-Appeal / Acknowledge cluster doesn't
                  flash before the latestAppeal lookup resolves. */}
              {(companyType === 'customer' || companyType === 'helix') && !loading && initialLoaded && (() => {
                const isOnTime = grnData.on_time_status === 'on_time';
                // hasPendingAppeal = a *supplier-raised* appeal awaiting
                // customer response. Customer-raised appeals are responded
                // to by the supplier — those don't surface a Respond button
                // to the customer.
                const hasPendingAppeal = latestAppeal?.status === 'awaiting_response'
                  && !latestAppeal?.is_customer_appeal;
                // hasActiveAppeal = any awaiting/queried appeal regardless of
                // side. We use this to suppress the Acknowledge + Appeal
                // cluster: a customer with their own pending appeal is
                // already waiting on the supplier, so re-offering
                // Acknowledge / Appeal would be confusing.
                const hasActiveAppeal = latestAppeal
                  && ['awaiting_response', 'queried'].includes(latestAppeal.status);
                // CR-4739 round 2: once the previous appeal is resolved
                // (rejected / accepted) the row is closed to further
                // action — no Acknowledge, no Appeal. Before this guard
                // the pop-up kept offering those buttons on rejected
                // rows because workflow_status stayed at pending_review.
                // CR-4678 round 2: 'cancelled' must NOT gate the buttons
                // off — a cancelled appeal is the supplier withdrawing
                // their own submission, and a re-appeal should be
                // possible. Jacqueline's bounce: the listing table
                // correctly re-showed the Appeal button after cancel
                // (via CR-4678 round 1) but this pop-up pathway still
                // hid it because of the overly-broad gate.
                const hasResolvedAppeal = latestAppeal
                  && ['rejected', 'accepted'].includes(latestAppeal.status);
                const isPendingReview = grnData.workflow_status === 'pending_review';
                const isDisputed = grnData.workflow_status === 'disputed';
                const isEarlyOrLate = grnData.on_time_status === 'early' || grnData.on_time_status === 'late';

                return (
                  <>
                    {/* Pending appeal from supplier → respond */}
                    {hasPendingAppeal && (
                      <button
                        onClick={() => setShowAppealResponseModal(true)}
                        disabled={loading}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                      >
                        <CheckCircle className="h-4 w-4" />
                        Respond to Appeal
                      </button>
                    )}

                    {/* Early/Late + no active or resolved appeal → Acknowledge + Appeal.
                        CR-4739 round 2: also suppress when a prior appeal
                        is rejected/accepted/cancelled — the row is done. */}
                    {!hasActiveAppeal && !hasResolvedAppeal && isPendingReview && isEarlyOrLate && (
                      <>
                        <button
                          onClick={() => setShowAcknowledgeModal(true)}
                          disabled={loading}
                          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                        >
                          <CheckCircle className="h-4 w-4" />
                          Acknowledge
                        </button>
                        {grnData.can_appeal && (
                          <button
                            onClick={() => setShowAppealModal(true)}
                            disabled={loading}
                            className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                          >
                            <AlertTriangle className="h-4 w-4" />
                            Appeal
                          </button>
                        )}
                      </>
                    )}

                    {/* CR-4649 round 2: On-Time + pending review intentionally
                        has no button — the delivery is fine, there's nothing
                        for the customer to act on. Keeping a bare Acknowledge
                        here (previous behaviour) confused reviewers into
                        thinking something was outstanding.
                        isOnTime check left only to silence unused-var lint. */}
                    {!hasPendingAppeal && isOnTime && false}

                    {/* Disputed → Resolve */}
                    {isDisputed && (
                      <button
                        onClick={() => setShowResolveModal(true)}
                        disabled={loading}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                      >
                        <CheckCheck className="h-4 w-4" />
                        Resolve Dispute
                      </button>
                    )}

                    {/* Customer cancel their own appeal */}
                    {latestAppeal && latestAppeal.is_customer_appeal && ['awaiting_response', 'queried'].includes(latestAppeal.status) && (
                      <button
                        onClick={async () => {
                          if (!window.confirm('Are you sure you want to cancel this appeal?')) return;
                          try {
                            setLoading(true);
                            await purchasingAPI.cancelDeliveryAppeal(latestAppeal.id);
                            toast.success('Appeal cancelled');
                            await handleAppealSuccess();
                          } catch (err: unknown) {
                            toast.error(getApiErrorMessage(err, 'Failed to cancel appeal'));
                          } finally {
                            setLoading(false);
                          }
                        }}
                        disabled={loading}
                        className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors disabled:opacity-50 flex items-center gap-2"
                      >
                        <X className="h-4 w-4" />
                        Cancel Appeal
                      </button>
                    )}
                  </>
                );
              })()}
            </div>

            <button
              onClick={onClose}
              disabled={loading}
              className="px-6 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg font-medium transition-colors disabled:opacity-50"
            >
              Close
            </button>
          </div>
        </div>
      </div>

      {/* GRN Workflow Modals */}
      <AcknowledgeGRNModal
        isOpen={showAcknowledgeModal}
        onClose={() => setShowAcknowledgeModal(false)}
        grnId={grnData.id}
        grnNumber={grnData.grn_number}
        partNumber={grnData.part_number || grnData.po_line_details?.part_number || '-'}
        poNumber={grnData.po_number || grnData.po_line_details?.po_details?.po_number || '-'}
        onSubmit={handleWorkflowSubmit}
      />

      <DisputeGRNModal
        isOpen={showDisputeModal}
        onClose={() => setShowDisputeModal(false)}
        grnId={grnData.id}
        grnNumber={grnData.grn_number}
        partNumber={grnData.part_number || grnData.po_line_details?.part_number || '-'}
        poNumber={grnData.po_number || grnData.po_line_details?.po_details?.po_number || '-'}
        onSubmit={handleWorkflowSubmit}
      />

      <ResolveGRNDisputeModal
        isOpen={showResolveModal}
        onClose={() => setShowResolveModal(false)}
        grnId={grnData.id}
        grnNumber={grnData.grn_number}
        partNumber={grnData.part_number || grnData.po_line_details?.part_number || '-'}
        poNumber={grnData.po_number || grnData.po_line_details?.po_details?.po_number || '-'}
        onSubmit={handleWorkflowSubmit}
      />

      {/* Appeal Modals */}
      <RaiseAppealModal
        isOpen={showAppealModal}
        onClose={() => setShowAppealModal(false)}
        grn={grnData}
        onSuccess={handleAppealSuccess}
        existingAppealId={latestAppeal?.status === 'queried' ? latestAppeal.id : undefined}
      />

      {latestAppeal && (
        <AppealResponseModal
          isOpen={showAppealResponseModal}
          onClose={() => setShowAppealResponseModal(false)}
          appeal={latestAppeal}
          onSuccess={handleAppealSuccess}
        />
      )}
    </>
  );
}
